/* DeleteAction.js */

"use strict";

const DEVICE_NAME_MAX_LENGTH = 60;
const DEVICE_NAME_LIST_LENGTH = 10;
const WARNING_CONTROLLED = "Sorry! Controlled devices cannot be deleted.\n\n";
const WARNING_DEVICE =
    "Deleting a device also deletes the device's configuration snapshots, logs, and OS plan results.\n\n";
const WARNING_CONTROLLER =
    "At least one controller is selected. " +
    "Deleting a controller also deletes its controlled devices, their configuration snapshots, logs, and OS plan results.\n\n";
class DeleteAction {
    constructor(logger, gameEditor, modalUserFeedback, activeView, userSession, DeviceEntity) {
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._modalUserFeedback = modalUserFeedback;
        this._activeView = activeView;
        this._activeOrgId = userSession.activeOrganization.id;
        this._DeviceEntity = DeviceEntity;
        this._controlledDevicesCount = 0;
        this._selectedDeviceIds = [];
        this._selectedDeviceNames = [];
        this._controllerDevicesCount = 0;

        if (this._activeView && this._activeView.pagingFeed) {
            this._selectedDeviceIds = this._activeView.getSelectedDeviceIds().selectedRows || [];
        }

        this.run = this.run.bind(this);
        this._deleteDevices = this._deleteDevices.bind(this);
    }

    run() {
        if (this._selectedDeviceIds.length) {
            return this._activeView.pagingFeed
                .waitForRangeIdsUpdated()
                .then(async () => {
                    const query = {
                        id: {$in: this._selectedDeviceIds},
                    };

                    const devices = await this._activeView._DeviceEntityDef.loadDevices(
                        this._activeView.activeOrgId,
                        query,
                        {
                            id: true,
                            name: true,
                            controlType: true,
                            baseControlType: true,
                        }
                    );

                    let controlledDevices = [],
                        controllerDevices = [];

                    this._selectedDeviceNames = devices.map((key) => {
                        if (
                            key.baseControlType === "CONTROLLED" ||
                            key.baseControlType === "HYBRID_CONTROLLED"
                        ) {
                            controlledDevices.push(key);
                        }
                        if (
                            key.baseControlType === "CONTROLLER" ||
                            key.baseControlType === "HYBRID_CONTROLLER"
                        ) {
                            controllerDevices.push(key);
                        }
                        return key.name;
                    });

                    this._controlledDevicesCount = controlledDevices.length || 0;
                    this._controllerDevicesCount = controllerDevices.length || 0;

                    this._controlledDeviceNames = controlledDevices.map((i) => i.name);
                    this._controlledDeviceIDs = controlledDevices.map((i) => i.id);

                    let message = "";
                    let confirmDetails = {
                            text: "Confirm",
                            type: "destructive",
                        },
                        rejectDetails = {
                            text: "Cancel",
                            type: "text",
                        };

                    const deviceCount =
                        this._selectedDeviceIds.length - this._controlledDevicesCount;
                    const s = deviceCount > 1 ? "s" : "";

                    if (this._controlledDevicesCount === 0 && this._controllerDevicesCount === 0) {
                        if (deviceCount < DEVICE_NAME_LIST_LENGTH) {
                            message += WARNING_DEVICE;
                            message += `Are you sure you want to delete the device${s}?\n`;
                            message += this._getDeviceList();
                        } else {
                            message += WARNING_DEVICE;
                            message += `Are you sure you want to delete the ${deviceCount} devices?\n`;
                        }
                    } else if (this._selectedDeviceIds.length === this._controlledDevicesCount) {
                        message += WARNING_CONTROLLED;
                        confirmDetails = {
                            text: "OK",
                            type: "destructive",
                            classes: "hidden",
                        };
                        rejectDetails = {
                            text: "OK",
                            type: "text",
                        };
                    } else if (
                        this._controlledDevicesCount > 0 &&
                        this._controllerDevicesCount > 0
                    ) {
                        message += WARNING_CONTROLLED;
                        message += WARNING_DEVICE;
                        message += WARNING_CONTROLLER;

                        if (deviceCount < DEVICE_NAME_LIST_LENGTH) {
                            message += `Are you sure you want to delete the device${s} and any controlled devices?\n`;
                            message += this._getDeviceList();
                        } else {
                            message += `Are you sure you want to delete the ${deviceCount} device${s} and any controlled devices?\n`;
                        }
                    } else if (
                        this._controlledDevicesCount > 0 &&
                        this._controllerDevicesCount === 0
                    ) {
                        message += WARNING_CONTROLLED;
                        message += WARNING_DEVICE;

                        if (deviceCount < DEVICE_NAME_LIST_LENGTH) {
                            message += `Are you sure you want to delete the device${s}?\n`;
                            message += this._getDeviceList();
                        } else {
                            message += `Are you sure you want to delete the ${deviceCount} device${s}?\n`;
                        }
                    } else if (
                        this._controlledDevicesCount === 0 &&
                        this._controllerDevicesCount > 0
                    ) {
                        message += WARNING_DEVICE;
                        message += WARNING_CONTROLLER;

                        if (deviceCount < DEVICE_NAME_LIST_LENGTH) {
                            message += `Are you sure you want to delete the device${s} and any controlled devices?\n`;
                            message += this._getDeviceList();
                        } else {
                            message += `Are you sure you want to delete the ${deviceCount} device${s} and any controlled devices?\n`;
                        }
                    }

                    return this._gameEditor
                        .runQuickConfirmationActionWorkflow(
                            {
                                message: message,
                                title: "Delete",
                                confirmDetails,
                                rejectDetails,
                            },
                            this._deleteDevices
                        )
                        .catch((err) => {
                            if (err.userRejected) {
                                return this._modalUserFeedback.hideDisplayed();
                            }
                            this._logger.warn({error: err}, "DeleteAction.run() - Error");
                            return this._modalUserFeedback.displayError({error: {errorMsg: err}});
                        });
                })
                .catch((err) => {
                    this._logger.warn(
                        {error: err},
                        "Something went wrong while retrieving devices - Error"
                    );
                });
        } else {
            return Promise.resolve();
        }
    }

    _deleteDevices() {
        const devicesToSend =
            this._controlledDevicesCount > 0
                ? this._selectedDeviceIds.filter((i) => this._controlledDeviceIDs.indexOf(i) === -1)
                : this._selectedDeviceIds;

        let workTarget = {
            method: "multi",
            ids: devicesToSend,
        };
        const showProgFunc = (prog) => {
            this._modalUserFeedback.showLoadingProgress(
                `${prog.successCount} of ${prog.targetCount}`
            );
        };
        return this._DeviceEntity
            .runDeleteDevicesWork(
                this._activeOrgId,
                workTarget,
                {launchAndForget: false},
                showProgFunc
            )
            .then((workResults) => {
                this._DeviceEntity.throwWorkErrors(workResults);
                this._activeView.resetSelection();
                return Promise.resolve();
            });
    }

    _getDeviceList() {
        let deviceList = "";
        const devices = this._selectedDeviceNames
            .filter((i) => this._controlledDeviceNames.indexOf(i) === -1)
            .splice(0, DEVICE_NAME_LIST_LENGTH);
        devices.forEach((deviceName) => {
            let shortDeviceName =
                deviceName.length > DEVICE_NAME_MAX_LENGTH
                    ? deviceName.substring(0, DEVICE_NAME_MAX_LENGTH) + "…"
                    : deviceName;
            deviceList += "\n" + shortDeviceName;
        });

        return deviceList;
    }
}

module.exports = DeleteAction;

DeleteAction.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.ui.userFeedback",
    "gameEditor.activeView",
    "$teve.userSession",
    "$teve.entities.Device",
];

DeleteAction.$displayDetails = {
    type: "Action",
    name: "DeleteAction",
    icon: "common.trash_bin",
    shown: true,
    title: "Delete devices",
    order: 17,
};
